import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import {
  Box,
  Stack,
  Typography,
  Button,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  Avatar,
  TableRow,
  Paper,
  Pagination,
  Menu,
  MenuItem,
  Rating,
  useTheme,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  TextField,
} from "@mui/material";
import { Search, MoreVert, DeleteOutline, Close } from "@mui/icons-material";

import { PATH_DASHBOARD } from "@app/routes/paths";
import { FormProvider, RHFTextField } from "@app/components";
import { Strings } from "@app/constants";
import { useRequestState } from "@app/hooks";
import { allCars, deleteProductById, product } from "../slice";
import { selectAllCarsDataState, selectDeleteProductState } from "../selector";
import { AssignCustomerModal } from "@app/modules/salesperson/view/assign-customer-modal";
import { SoldCarModal } from "./soldCar";

const Product = () => {
  const methods = useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRow, setSelectedRow] = useState<number | null>(null);
  const [page, setPage] = useState<number>(1);
  const [search, setSearch] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<string>("All");
  const [openModal, setOpenModal] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [carToDelete, setCarToDelete] = useState<any>(null);

  const itemsPerPage = 10;

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, id: number) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const { loading: isDeleting } = useRequestState({
    stateSelector: selectDeleteProductState,
    successMessageShown: true,
    errorShown: true,
    onSuccess: () => {
      setOpenDeleteModal(false);
      setCarToDelete(null);
    },
  });
  const { data: allProductDataView } = useRequestState({
    stateSelector: selectAllCarsDataState,
    errorShown: true,
  });
  useEffect(() => {
    dispatch(allCars());
  }, [dispatch]);
  const displayedProducts = Array.isArray(allProductDataView)
    ? allProductDataView
    : [];

  const getCarStatus = (item: any): "Available for Sale" | "Not Available for Sale" | "Sold" => {
    const isSold =
      item?.isSold === true ||
      item?.sold === true ||
      item?.status?.toLowerCase() === "sold" ||
      item?.isSoldCar === true ||
      Boolean(item?.soldCar) ||
      Boolean(item?.soldAt);

    if (isSold) {
      return "Sold";
    }

    const isAvailableForSale =
      item?.carAvailableForSell === true ||
      item?.isAvailableForSell === true ||
      item?.isAvailable === true ||
      item?.status?.toLowerCase() === "available" ||
      item?.status?.toLowerCase() === "available for sale" ||
      item?.status?.toLowerCase() === "available for sell" ||
      item?.carAvailableForSell === "true" ||
      item?.isAvailableForSell === "true";

    if (isAvailableForSale) {
      return "Available for Sale";
    }

    return "Not Available for Sale";
  };

  const filteredData = displayedProducts?.filter((item) => {
    const searchTerm = search.toLowerCase();

    const includes = (field: any) =>
      String(field || "")
        .toLowerCase()
        .includes(searchTerm);

    const matchesSearch =
      includes(item?.brand?.name) ||
      includes(item?.model?.name) ||
      includes(item?.transmission) ||
      includes(item?.engineType) ||
      includes(item?.manufacturingYear) ||
      includes(item?.driven) ||
      includes(item?.price) ||
      includes(item?.name) ||
      includes(item?.category?.name) ||
      includes(item?.avgRating);

    const carStatus = getCarStatus(item);
    const matchesStatus =
      selectedStatus === "All" || carStatus === selectedStatus;

    return matchesSearch && matchesStatus;
  });

  const totalFilteredItems = filteredData.length;
  const totalPages = Math.ceil(totalFilteredItems / itemsPerPage) || 1;

  useEffect(() => {
    if (page > totalPages) {
      setPage(1);
    }
  }, [totalPages, page]);

  const paginatedData = filteredData.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1);
  };

  useEffect(() => {
    dispatch(
      product({
        limit: 100,
        page: 1,
        search: "",
      })
    );
  }, [dispatch]);

  const fontFamily = Strings.fontLanguage.workSans;

  return (
    <FormProvider methods={methods}>
      <Stack direction="row" sx={{ minHeight: "100vh", bgcolor: "#F5F5F5" }}>
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", p: { xs: 1.5, sm: 2 } }}>
          <Box sx={{ flex: 1, overflowY: "auto", pt: 1 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", sm: "center" }}
              flexWrap={{ xs: "wrap", sm: "nowrap" }}
              rowGap={2}
              mb={3}
            >
              <RHFTextField
                name="Search"
                placeholder="Search"
                size="small"
                value={search}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="flex-end"
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                <TextField
                  select
                  size="small"
                  label="Status"
                  value={selectedStatus}
                  onChange={(e) => {
                    setSelectedStatus(e.target.value);
                    setPage(1);
                  }}
                  sx={{
                    minWidth: { xs: "100%", sm: 235 },
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#fff",
                      borderRadius: 1,
                    },
                    "& .MuiSelect-select": {
                      whiteSpace: "nowrap",
                    },
                  }}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        sx: { minWidth: 235 },
                      },
                    },
                  }}
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="Available for Sale">Available for Sale</MenuItem>
                  <MenuItem value="Not Available for Sale">Not Available for Sale</MenuItem>
                  <MenuItem value="Sold">Sold</MenuItem>
                </TextField>
                <Button
                  variant="contained"
                  sx={{
                    width: 150,
                    height: 40,
                    textTransform: "none",
                    backgroundColor: theme.palette.primary.main,
                    color: "white",
                    whiteSpace: "nowrap",
                  }}
                  onClick={() => navigate(PATH_DASHBOARD.addProduct1)}
                >
                  {Strings.button.addCar}
                </Button>
              </Stack>
            </Stack>

            <Stack
              sx={{
                width: { xs: "calc(100vw - 48px)", sm: "calc(100vw - 288px)" },
                overflow: "hidden",
              }}
            >
              <TableContainer component={Paper} sx={{ pb: 3 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      {[
                        "Image",
                        "Brand",
                        "Model",
                        "Manufacturing Year",
                        "Engine Type",
                        "Car Travelled",
                        "Price",
                        "Rating",
                        "Status",
                        "Action",
                      ].map((heading: any) => (
                        <TableCell
                          key={heading}
                          sx={{
                            fontWeight: 600,
                            fontFamily: "Poppins",
                            color: "#474747",
                            minWidth: [
                              "Manufacturing Year",
                              "Engine Type",
                              "Car Travelled",
                              "Status",
                            ].includes(heading)
                              ? 180
                              : 110,
                          }}
                        >
                          {heading}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedData.length > 0 ? (
                      paginatedData.map((item: any, index: any) => (
                        <TableRow
                          key={item.id || index}
                          onClick={() => {
                            handleMenuClose();
                            navigate(`${PATH_DASHBOARD.productDetail}`, {
                              state: { productId: item.id },
                            });
                          }}
                          sx={{
                            cursor: "pointer",
                            "&:hover": {
                              backgroundColor: "#E3F2FD",
                            },
                            "&.Mui-selected, &.Mui-selected:hover": {
                              backgroundColor: "#BBDEFB",
                            },
                          }}
                        >
                          <TableCell
                            sx={{
                              fontFamily: "Poppins",
                              fontWeight: 500,
                              color: "#5F5F5F",
                              pl: "4",
                            }}
                          >
                            <Avatar
                              sx={{
                                width: 30,
                                height: 30,
                                right: 12,
                                fontSize: 14,
                              }}
                              src={
                                item.images?.[0]?.url ||
                                "https://randomuser.me/api/portraits/men/75.jpg"
                              }
                            >
                              {item?.name?.charAt(0)?.toUpperCase() ||
                                item?.brand?.name?.charAt(0)?.toUpperCase() ||
                                "?"}
                            </Avatar>
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: "Poppins",
                              fontWeight: 500,
                              color: "#5F5F5F",
                            }}
                          >
                            {item?.brand?.name || "N/A"}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: "Poppins",
                              fontWeight: 500,
                              color: "#5F5F5F",
                            }}
                          >
                            {item?.model?.name || "N/A"}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: "Poppins",
                              fontWeight: 500,
                              color: "#5F5F5F",
                            }}
                          >
                            {item?.manufacturingYear || "N/A"}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: "Poppins",
                              fontWeight: 500,
                              color: "#5F5F5F",
                            }}
                          >
                            {item?.engineType || "N/A"}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: "Poppins",
                              fontWeight: 500,
                              color: "#5F5F5F",
                            }}
                          >
                            {item?.driven || "N/A"}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: "Poppins",
                              fontWeight: 500,
                              color: "#5F5F5F",
                            }}
                          >
                            {item?.price != null
                              ? Number(item.price).toLocaleString("en-IN", {
                                  currency: "INR",
                                  style: "currency",
                                  maximumFractionDigits: 0,
                                })
                              : "N/A"}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: "Poppins",
                              fontWeight: 500,
                              color: "#5F5F5F",
                            }}
                          >
                            <Rating
                              name={`rating-${item?.id}`}
                              value={Number(item?.avgRating) || 0}
                              precision={0.5} // allows half stars if needed
                              readOnly
                              size="small"
                            />
                          </TableCell>

                          <TableCell
                            sx={{
                              fontFamily: "Poppins",
                              fontWeight: 500,
                              color: "#5F5F5F",
                            }}
                          >
                            <Box
                              sx={{
                                display: "inline-block",
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 1,
                                fontWeight: 600,
                                fontSize: 12,
                                whiteSpace: "nowrap",
                                ...(getCarStatus(item) === "Available for Sale" && {
                                  backgroundColor: theme.palette.success.lighter || "#E8F5E9",
                                  color: theme.palette.success.main || "#2E7D32",
                                }),
                                ...(getCarStatus(item) === "Not Available for Sale" && {
                                  backgroundColor: theme.palette.warning.lighter || "#FFF3E0",
                                  color: theme.palette.warning.main || "#E65100",
                                }),
                                ...(getCarStatus(item) === "Sold" && {
                                  backgroundColor: theme.palette.error.lighter || "#FFEBEE",
                                  color: theme.palette.error.main || "#C62828",
                                }),
                              }}
                            >
                              {getCarStatus(item)}
                            </Box>
                          </TableCell>

                          <TableCell>
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMenuOpen(e, item.id);
                              }}
                            >
                              <MoreVert />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={10}
                          align="center"
                          sx={{
                            fontWeight: 600,
                            fontFamily: "Poppins",
                            color: "#474747",
                          }}
                        >
                          {search || selectedStatus !== "All"
                            ? "No Cars found matching your criteria"
                            : "No Cars found"}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {totalFilteredItems > 0 && (
                <Stack
                  direction="row"
                  justifyContent="flex-end"
                  alignItems="center"
                  spacing={2}
                  mt={2}
                >
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontFamily: fontFamily,
                      fontWeight: 400,
                    }}
                  >
                    {`Showing ${
                      filteredData?.length === 0
                        ? 0
                        : (page - 1) * itemsPerPage + 1
                    } to ${Math.min(
                      page * itemsPerPage,
                      filteredData?.length
                    )} of ${filteredData.length} Cars`}
                  </Typography>
                  <Pagination
                    count={Math.ceil(filteredData.length / itemsPerPage)}
                    page={page}
                    onChange={(event, value) => setPage(value)}
                    shape="rounded"
                  />
                </Stack>
              )}
            </Stack>
          </Box>
        </Box>
      </Stack>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            handleMenuClose();
            navigate(`${PATH_DASHBOARD.editProduct}`, {
              state: { productId: selectedRow },
            });
          }}
        >
          {Strings.button.edit}
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            const rowData = paginatedData?.find(
              (item) => item?.id === selectedRow
            );
            setSelectedRowData(rowData);
            setOpenModal(true);
          }}
        >
          {Strings.button.sold}
        </MenuItem>
        <MenuItem
          onClick={() => {
            const rowData = paginatedData?.find(
              (item) => item?.id === selectedRow
            );
            setCarToDelete(rowData || { id: selectedRow });
            setOpenDeleteModal(true);
            handleMenuClose();
          }}
          sx={{ color: "error.main" }}
        >
          {Strings.button.delete}
        </MenuItem>
      </Menu>
      {openModal && (
        <SoldCarModal
          open={openModal}
          onClose={() => setOpenModal(false)}
          selectedRow={selectedRowData}
        />
      )}

      {/* ------------------------------------------------------------- */}
      {/* DELETE CAR CONFIRMATION POPUP */}
      {/* ------------------------------------------------------------- */}
      <Dialog
        open={openDeleteModal}
        onClose={() => !isDeleting && setOpenDeleteModal(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 1,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography
              variant="h6"
              sx={{
                fontFamily: "Poppins",
                fontWeight: 600,
                color: "#101828",
              }}
            >
              Delete Car
            </Typography>
            <IconButton
              disabled={isDeleting}
              onClick={() => {
                setOpenDeleteModal(false);
                setCarToDelete(null);
              }}
              size="small"
              sx={{ color: theme.palette.grey[500] }}
            >
              <Close sx={{ fontSize: 20 }} />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <DialogContentText
            sx={{
              mb: 2,
              fontFamily,
              color: "#474747",
              fontSize: "0.9rem",
              lineHeight: 1.5,
            }}
          >
            Are you sure you want to delete this car? This action cannot be undone.
          </DialogContentText>

          {carToDelete && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 1.5,
                backgroundColor: theme.palette.grey[50],
                border: `1px solid #E0E0E0`,
              }}
            >
              <Stack spacing={1.2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" sx={{ fontFamily, color: "#5F5F5F", fontWeight: 500 }}>
                    Car:
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily, color: "#101828", fontWeight: 600 }}>
                    {[
                      carToDelete?.brand?.name,
                      carToDelete?.model?.name,
                      carToDelete?.manufacturingYear
                        ? `(${carToDelete?.manufacturingYear})`
                        : "",
                    ]
                      .filter(Boolean)
                      .join(" ") ||
                      carToDelete?.name ||
                      `#${carToDelete?.id}`}
                  </Typography>
                </Stack>
                {carToDelete?.price && (
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" sx={{ fontFamily, color: "#5F5F5F", fontWeight: 500 }}>
                      Price:
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily, color: theme.palette.primary.main, fontWeight: 700 }}>
                      {Number(carToDelete.price).toLocaleString("en-IN", {
                        currency: "INR",
                        style: "currency",
                        maximumFractionDigits: 0,
                      })}
                    </Typography>
                  </Stack>
                )}
                {carToDelete?.transmission && (
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" sx={{ fontFamily, color: "#5F5F5F", fontWeight: 500 }}>
                      Transmission:
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily, color: "#101828", fontWeight: 600 }}>
                      {carToDelete.transmission}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Paper>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: 1.5 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setOpenDeleteModal(false);
              setCarToDelete(null);
            }}
            disabled={isDeleting}
            sx={{
              flex: 1,
              borderRadius: 1,
              textTransform: "none",
              fontFamily,
              fontWeight: 600,
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              "&:hover": {
                borderColor: theme.palette.primary.dark,
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
              },
            }}
          >
            {Strings.button.cancel}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (carToDelete?.id) {
                dispatch(deleteProductById({ productId: carToDelete.id }));
              }
            }}
            disabled={isDeleting}
            startIcon={
              isDeleting ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
            sx={{
              flex: 1,
              borderRadius: 1,
              textTransform: "none",
              fontFamily,
              fontWeight: 600,
              backgroundColor: theme.palette.primary.main,
              color: "white",
              "&:hover": {
                backgroundColor: theme.palette.primary.dark,
              },
            }}
          >
            {isDeleting ? "Deleting..." : Strings.button.delete}
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
};

export { Product };
